/**
 * RecruiterPreScreenReview - recruiter detail view of ONE shared pre-screen.
 *
 * Reuses the exact candidate detail UI (InterviewResultView: scorecard,
 * recording, transcript, artifacts) but fetches via the recruiter-gated
 * endpoint, which the gateway enforces against the candidate's per-type
 * visibility. A hidden / non-shared attempt returns 404.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Video } from 'lucide-react';

import { useAuth } from '../../hooks/useAuth';
import { useLanguage } from '../../contexts/LanguageContext';
import { getRecruiterPreScreenDetail, type ResultDetail } from '../../api/interview';
import { InterviewResultView } from '../mock/MockReview';
import {
  RecruiterInlineEmpty,
  RecruiterLoadingScreen,
  RecruiterPageShell,
} from '../../components/Recruiter/RecruiterStates';

const RecruiterPreScreenReview: React.FC = () => {
  const { candidateId, sessionId } = useParams<{ candidateId: string; sessionId: string }>();
  const navigate = useNavigate();
  const { language: lang } = useLanguage();
  const { apiCall, isLoading: authLoading, isSignedIn } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ResultDetail | null>(null);

  const load = useCallback(async (silent = false) => {
    if (!candidateId || !sessionId) return;
    try {
      if (!silent) setLoading(true);
      setError(null);
      const d = await getRecruiterPreScreenDetail(apiCall, candidateId, sessionId);
      if (silent) {
        setData((prev) => (prev ? { ...prev, evaluation: d.evaluation, summary: d.summary } : d));
      } else {
        setData(d);
      }
    } catch (e) {
      if (!silent) setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [apiCall, candidateId, sessionId]);

  useEffect(() => {
    if (authLoading) return;
    if (!isSignedIn) {
      navigate('/login');
      return;
    }
    void load();
  }, [authLoading, isSignedIn, load, navigate]);

  // Poll while the scorecard is still being generated server-side.
  useEffect(() => {
    if (!data) return undefined;
    if (data.evaluation) return undefined;
    if (data.summary.status !== 'completed') return undefined;
    let attempts = 0;
    const interval = window.setInterval(() => {
      attempts += 1;
      if (attempts > 30) {
        window.clearInterval(interval);
        return;
      }
      void load(true);
    }, 6000);
    return () => window.clearInterval(interval);
  }, [data, load]);

  const de = lang === 'de';
  const title = de ? 'Intro-Ergebnis' : 'Intro result';
  const backLabel = de ? 'Zurück' : 'Back';
  const privacyFooter = de
    ? 'Von der Kandidatin/dem Kandidaten freigegebenes Intro-Ergebnis. Aufnahme und Transkript sind nur für berechtigte Recruiter:innen sichtbar und werden nach 90 Tagen automatisch gelöscht.'
    : 'An Intro result the candidate chose to share. Recording and transcript are visible to authorised recruiters only and are auto-deleted after 90 days.';

  if (loading || authLoading) {
    return <RecruiterLoadingScreen icon={Video} />;
  }

  if (error || !data) {
    return (
      <RecruiterPageShell contentClassName="max-w-5xl">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mb-6 inline-flex items-center gap-1.5 text-sm text-slate-600 transition-colors hover:text-slate-900"
        >
          <ArrowLeft className="size-4" />
          {backLabel}
        </button>
        <RecruiterInlineEmpty
          icon={AlertTriangle}
          title={de ? 'Intro nicht verfügbar' : 'Intro unavailable'}
          description={
            de
              ? 'Dieses Ergebnis wurde nicht freigegeben oder ist nicht mehr verfügbar.'
              : 'This result is not shared or is no longer available.'
          }
        />
      </RecruiterPageShell>
    );
  }

  return (
    <InterviewResultView
      data={data}
      lang={de ? 'de' : 'en'}
      title={title}
      backLabel={backLabel}
      onBack={() => navigate(-1)}
      privacyFooter={privacyFooter}
    />
  );
};

export default RecruiterPreScreenReview;
